import { useCallback, useEffect, useRef } from 'react';
import type { AlertVisibility } from '@kirin/api-client/contracts/alerts';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { writeAlertVisibilityToStorage } from '@/lib/alertVisibilityStorage';
import { trackEvent } from '@/lib/analytics';

/**
 * Map the `scopeKey` prop (e.g. `'account'` or `'org:abc-123'`) to the
 * coarse-grained `'organization' | 'account'` taxonomy used by the
 * suppression-action analytics events (`alert_muted`, `alert_excluded`,
 * etc.). Keeps `alerts_visibility_changed` slice-able alongside the
 * existing per-action events without leaking the org id.
 */
function resolveAnalyticsScope(scopeKey: string): 'organization' | 'account' {
  return scopeKey === 'account' ? 'account' : 'organization';
}

export interface AlertVisibilitySelectorProps {
  /**
   * Stable scope identifier used to build the localStorage key (e.g.
   * `'org:abc-123'` or `'account'`). Different scopes keep independent
   * preferences.
   */
  scopeKey: string;
  /** Current value (controlled). */
  value: AlertVisibility;
  /**
   * Called with the new value whenever the user picks an option.
   * Persistence to `localStorage` happens inside this component as a side
   * effect; the caller is only responsible for updating its own
   * `visibility` state so the underlying query refetches.
   */
  onChange: (next: AlertVisibility) => void;
  /** Optional className passed to the wrapper. */
  className?: string;
}

/**
 * Per-scope 3-option visibility selector for the alerts table (KIR-2512).
 * Replaces the two boolean toggles (`ShowMutedAlertsToggle`,
 * `ShowExcludedAlertsToggle`) with a single segmented control.
 *
 * - Renders `null` while feature flags are loading or when BOTH
 *   `alert_muting` and `alert_ignore` are off, so flag-off behavior stays
 *   byte-identical to today (no toolbar shift).
 * - Persists its value to `localStorage` under
 *   `alertVisibility_${scopeKey}` so the user's preference survives
 *   reloads. See `alertVisibilityStorage.ts` for the legacy migration path
 *   that seeds `'all'` when one of the old toggles was previously on.
 * - The component is fully controlled: callers track the
 *   `AlertVisibility` themselves and feed it back via `value`. This keeps
 *   state ownership in the table container where it can also drive the
 *   React Query key + fetch.
 */
export function AlertVisibilitySelector({
  scopeKey,
  value,
  onChange,
  className,
}: AlertVisibilitySelectorProps): React.ReactElement | null {
  const { flags, isLoading: flagsLoading } = useFeatureFlags([
    flagsmithFeatureFlags.ALERT_MUTING,
    flagsmithFeatureFlags.ALERT_IGNORE,
  ]);
  const mutingEnabled = flags[flagsmithFeatureFlags.ALERT_MUTING] === true;
  const ignoreEnabled = flags[flagsmithFeatureFlags.ALERT_IGNORE] === true;
  const anyEnabled = mutingEnabled || ignoreEnabled;

  // Track whether the user has actually interacted with the selector. We
  // intentionally do NOT persist the initial value on mount so a freshly
  // visited scope does not eagerly stamp `'active'` into `localStorage`,
  // which would also defeat the legacy-migration path in
  // `alertVisibilityStorage.ts`.
  const userInteractedRef = useRef(false);

  useEffect(() => {
    if (!anyEnabled) return;
    if (!userInteractedRef.current) return;
    writeAlertVisibilityToStorage(scopeKey, value);
  }, [value, anyEnabled, scopeKey]);

  const handleValueChange = useCallback(
    (next: string): void => {
      // Radix Tabs gives us a string; the trigger values are typed
      // statically below so this cast is safe at runtime.
      userInteractedRef.current = true;
      const nextValue = next as AlertVisibility;
      // Mixpanel: emit before the controlled state propagates so we don't
      // depend on the parent's render cycle to ship the event. Mirrors the
      // taxonomy of the per-action mute/exclude events (KIR-2512 +
      // mixpanel-events.md User Action Events block).
      trackEvent('alerts_visibility_changed', {
        scope: resolveAnalyticsScope(scopeKey),
        value: nextValue,
      });
      onChange(nextValue);
    },
    [onChange, scopeKey]
  );

  if (flagsLoading || !anyEnabled) {
    return null;
  }

  return (
    <Tabs
      value={value}
      onValueChange={handleValueChange}
      className={className}
      data-slot="alert-visibility-selector"
    >
      <TabsList aria-label="Alert visibility">
        <TabsTrigger value="active" aria-label="Show active alerts only">
          Active
        </TabsTrigger>
        <TabsTrigger
          value="all"
          aria-label="Show all alerts including muted and excluded"
        >
          All
        </TabsTrigger>
        <Tooltip>
          {/* `<span>` separates two `Slot`-using components that both
              write `data-state` to the same node — without it, tooltip's
              `data-state="closed"` overwrites tab's `data-state="active"`
              and the selected-pill styling never lands (KIR-2606). */}
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <TabsTrigger
                value="suppressed"
                aria-label="Show suppressed alerts only"
              >
                Suppressed
              </TabsTrigger>
            </span>
          </TooltipTrigger>
          <TooltipContent>Muted and Excluded alerts only</TooltipContent>
        </Tooltip>
      </TabsList>
    </Tabs>
  );
}

AlertVisibilitySelector.displayName = 'AlertVisibilitySelector';
